import {testIdProps} from '@github-ui/test-id-props'
import {CalendarIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useMemo} from 'react'

import {useOnAnchorAction} from '../hooks/use-on-anchor-action'
import type {AnchorElement, AnchorVariant, RenderAnchor} from '../types'
import {formatSelection} from '../utils/parser'
import styles from './Anchor.module.css'
import {DatePickerTextInput} from './Input'
import {useDatePickerContext} from './Provider'

interface ExternalDatePickerAnchorProps {
  render: RenderAnchor
  children: React.ReactNode
  ref?: React.Ref<HTMLElement>
}

export const ExternalDatePickerAnchor = ({ref, render, children}: ExternalDatePickerAnchorProps) => {
  const onAction = useOnAnchorAction()
  const {
    configuration: {disabled},
  } = useDatePickerContext()

  // eslint-disable-next-line react-hooks/refs
  return render({
    ref,
    'aria-haspopup': 'true',
    tabIndex: 0,
    onClick: onAction,
    onKeyDown: onAction,
    children,
    disabled,
    'aria-disabled': disabled,
  })
}

ExternalDatePickerAnchor.displayName = 'ExternalDatePickerAnchor'

const StyledCalendarIcon = () => <Octicon icon={CalendarIcon} className={styles.calendarIcon} />

const DefaultDatePickerAnchor = ({
  ref,
  anchorVariant,
  children,
}: {
  anchorVariant: AnchorVariant
  children: string
  ref?: React.Ref<AnchorElement>
}) => {
  const {
    configuration: {iconPlacement, anchorClassName, fieldName, disabled},
    selection,
  } = useDatePickerContext()

  const onAction = useOnAnchorAction()

  const dateSelected = useMemo(() => !!selection, [selection])

  const ariaLabel = dateSelected
    ? `${fieldName} - Open date picker (currently selected: ${children})`
    : `${fieldName} - Choose date...`

  return anchorVariant === 'input' ? (
    <DatePickerTextInput anchorRef={ref} fullWidth className={anchorClassName} disabled={disabled} />
  ) : anchorVariant === 'icon-only' ? (
    <IconButton
      onClick={onAction}
      onKeyDown={onAction}
      type="button"
      className={anchorClassName}
      {...testIdProps('anchor-button')}
      icon={CalendarIcon}
      ref={ref}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  ) : (
    <Button
      aria-label={ariaLabel}
      onClick={onAction}
      onKeyDown={onAction}
      type="button"
      className={anchorClassName}
      {...testIdProps('anchor-button')}
      ref={ref}
      disabled={disabled}
      leadingVisual={iconPlacement === 'start' ? StyledCalendarIcon : undefined}
      trailingVisual={iconPlacement === 'end' ? StyledCalendarIcon : undefined}
    >
      {children}
    </Button>
  )
}

DefaultDatePickerAnchor.displayName = 'DefaultDatePickerAnchor'

export const DatePickerAnchor = () => {
  const {
    configuration: {anchor, placeholder, variant, dateFormat},
    anchorRef,
    selection,
  } = useDatePickerContext()

  const formattedSelection = useMemo(
    () =>
      formatSelection({
        selection,
        dateFormat,
        placeholder,
        rawFormat: false,
        variant,
      }),
    [placeholder, variant, dateFormat, selection],
  )

  if (anchor === null) return null

  if (typeof anchor === 'function')
    return (
      <ExternalDatePickerAnchor render={anchor} ref={anchorRef}>
        {formattedSelection}
      </ExternalDatePickerAnchor>
    )

  return (
    <DefaultDatePickerAnchor anchorVariant={anchor} ref={anchorRef}>
      {formattedSelection}
    </DefaultDatePickerAnchor>
  )
}
